import React, { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery } from '@apollo/client';
import { toast } from 'react-toastify';
import { RuleForm } from '../components/ui/RuleForm';
import {
  ADD_ALLOCATION_CONDITION,
  FETCH_ALLOCATION_RULE,
  REMOVE_ALLOCATION_CONDITION,
  UPDATE_ALLOCATION_RULE,
} from '../../../api/queries/allocation';
import { stagingOptions } from '../../../constants/apollo';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';

const truncateToastText = (text: string | undefined | null, maxLength: number = 12): string => {
  if (!text) return text || '';
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength) + '...';
};

const EditRule = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { data, loading } = useQuery(FETCH_ALLOCATION_RULE, {
    ...stagingOptions,
    variables: { id },
    fetchPolicy: 'network-only',
  });
  const [updateRule] = useMutation(UPDATE_ALLOCATION_RULE, stagingOptions);
  const [addCondition] = useMutation(ADD_ALLOCATION_CONDITION, stagingOptions);
  const [removeCondition] = useMutation(REMOVE_ALLOCATION_CONDITION, stagingOptions);
  const [saving, setSaving] = useState(false);

  const rule = data?.fetchAllocationRule;
  const ruleNotFound = !loading && !rule;

  const initialData = useMemo(() => {
    if (!rule) return undefined;

    return {
      name: rule.name,
      priority: rule.priority,
      resultVia: rule.resultVia,
      resultData: rule.resultData || '',
      isActive: rule.isActive,
      description: rule.description || '',
      conditions: (rule.conditions || []).map((condition: any, index: number) => ({
        id: `${condition.param}-${index}`,
        parameter: condition.param,
        operator: condition.operator,
        value: condition.value,
        castType: condition.cast || 'string',
      })),
    };
  }, [rule]);

  const handleSave = async (formData: any) => {
    try {
      setSaving(true);

      const currentRule = rule || data?.fetchAllocationRule;
      const existingConditions = currentRule?.conditions || [];
      const formConditions = formData.conditions || [];

      await updateRule({
        variables: {
          input: {
            id,
            name: formData.name,
            priority: formData.priority === '' ? 0 : parseInt(formData.priority, 10),
            resultVia: formData.resultVia,
            resultData: formData.resultData,
            description: formData.description || undefined,
            isActive: formData.isActive,
          },
        },
      });

      if (existingConditions.length > 0) {
        for (let index = existingConditions.length - 1; index >= 0; index -= 1) {
          await removeCondition({
            variables: {
              ruleId: id,
              conditionIndex: index,
            },
          });
        }
      }

      if (formConditions.length > 0) {
        for (const condition of formConditions) {
          await addCondition({
            variables: {
              ruleId: id,
              param: condition.parameter,
              operator: condition.operator,
              value: condition.value,
              cast: condition.castType || 'string',
            },
          });
        }
      }

      toast.success(
        <div>
          <div className="font-semibold">Allocation rule updated successfully!</div>
          <div className="text-sm mt-1">
            <strong>Name:</strong> {truncateToastText(formData.name, 12)} | <strong>ID:</strong> {id}
          </div>
        </div>
      );
      navigate('/allocation', { state: { refresh: true } });
    } catch (error: any) {
      console.error('Failed to update rule', error);
      toast.error(error?.graphQLErrors?.[0]?.message || error?.message || 'Unable to update rule.');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    navigate('/allocation');
  };

  return (
    <div className="flex flex-col space-y-2 bg-white pb-12">
      <div className="main-wrapper">
        <div className="w-full px-4 sm:px-6 lg:px-8">
          <button
            onClick={() => {
              if (window.history.length > 1) {
                navigate(-1);
              } else {
                navigate('/allocation');
              }
            }}
            className="flex items-center gap-2 text-gray-600 hover:text-primaryColor transition-colors mb-6 group"
          >
            <ArrowBackIcon
              sx={{ fontSize: '20px' }}
              className="group-hover:translate-x-[-2px] transition-transform"
            />
            <span className="text-sm font-medium">Back</span>
          </button>

          <div className="mb-8">
            <div className="bg-gradient-to-r from-primaryColor via-primaryColor to-darkBlue rounded-2xl p-8 shadow-xl relative overflow-hidden">
              <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full -mr-32 -mt-32"></div>
              <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/5 rounded-full -ml-24 -mb-24"></div>

              <div className="relative z-10">
                <div className="flex items-start gap-4">
                  <div className="w-16 h-16 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center shadow-2xl border border-white/30">
                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M11 4H4C3.46957 4 2.96086 4.21071 2.58579 4.58579C2.21071 4.96086 2 5.46957 2 6V20C2 20.5304 2.21071 21.0391 2.58579 21.4142C2.96086 21.7893 3.46957 22 4 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V13" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                      <path d="M18.5 2.50023C18.8978 2.10243 19.4374 1.87891 20 1.87891C20.5626 1.87891 21.1022 2.10243 21.5 2.50023C21.8978 2.89804 22.1213 3.43762 22.1213 4.00023C22.1213 4.56284 21.8978 5.10243 21.5 5.50023L12 15.0002L8 16.0002L9 12.0002L18.5 2.50023Z" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <h1 className="text-3xl font-bold text-white">Edit Strategy</h1>
                      <span className="px-3 py-1 bg-white/20 backdrop-blur-sm rounded-full text-xs font-semibold text-white border border-white/30">
                        ID: {id}
                      </span>
                    </div>
                    <p className="text-base text-white/90 leading-relaxed max-w-2xl">
                      Modify existing allocation logic. Ensure conditions are accurate to prevent routing errors in live operations.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl">
            <div className="max-w-4xl mx-auto">
              {loading ? (
                <div className="bg-white rounded-xl border border-gray-200 p-6 text-center text-gray-500">
                  Loading strategy details...
                </div>
              ) : ruleNotFound ? (
                <div className="bg-white rounded-xl border border-red-200 p-6 text-center text-red-600">
                  Unable to load this allocation strategy. Please try again.
                </div>
              ) : (
                <RuleForm initialData={initialData} onSave={handleSave} onCancel={handleCancel} isSaving={saving} />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditRule;

